import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import TurnstileWidget from "@/components/TurnstileWidget";
import api from "@/lib/api";

export default function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Turnstile : le backend exige un jeton à la connexion quand il est activé.
  const [turnstileEnabled, setTurnstileEnabled] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState("");
  const [turnstileToken, setTurnstileToken] = useState(null);

  useEffect(() => {
    api.get("/site-config").then(({ data }) => {
      setTurnstileEnabled(data.turnstile_enabled || false);
      setTurnstileSiteKey(data.turnstile_site_key || "");
    }).catch(() => {});
  }, []);

  const handleTurnstileToken = useCallback((token) => setTurnstileToken(token), []);
  const clearTurnstileToken = useCallback(() => setTurnstileToken(null), []);

  const submit = async (e) => {
    e.preventDefault();
    if (turnstileEnabled && !turnstileToken) {
      setError("Veuillez compléter la vérification anti-robot.");
      return;
    }
    setError("");
    setLoading(true);
    const { ok, error: err } = await login(email, password, remember, turnstileToken);
    setLoading(false);
    if (!ok) setError(err);
    // On success, AdminLayout re-renders with the authenticated user.
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <Link to="/" aria-label="Déstockage Luminaires — accueil" className="inline-flex"><Logo to={null} size="lg" variant="light" testId="admin-login-logo" /></Link>
        <p className="text-[10px] uppercase tracking-[0.25em] text-cream/40 mt-1 mb-8">Espace administrateur</p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-cream/50">Email</label>
            <Input data-testid="admin-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 h-12 rounded-sm bg-white/5 border-white/10 text-cream placeholder:text-cream/30" placeholder="admin@..." />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-cream/50">Mot de passe</label>
            <Input data-testid="admin-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-2 h-12 rounded-sm bg-white/5 border-white/10 text-cream placeholder:text-cream/30" placeholder="••••••••" />
          </div>
          <label className="flex items-center gap-2 text-sm text-cream/60 cursor-pointer select-none">
            <input
              data-testid="admin-login-remember-checkbox" type="checkbox" checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 rounded-sm border-white/10 accent-gold"
            />
            Se souvenir de moi pendant 30 jours
          </label>
          {turnstileEnabled && turnstileSiteKey && (
            <TurnstileWidget
              siteKey={turnstileSiteKey}
              onToken={handleTurnstileToken}
              onExpire={clearTurnstileToken}
              onError={clearTurnstileToken}
            />
          )}
          {error && <p data-testid="admin-login-error" className="text-sm text-destructive">{error}</p>}
          <button data-testid="admin-login-submit" type="submit" disabled={loading || (turnstileEnabled && !turnstileToken)} className="w-full bg-gold text-ink py-4 uppercase tracking-[0.15em] text-xs hover:bg-cream transition-colors disabled:opacity-60">
            {loading ? "Connexion..." : "Se connecter"}
          </button>
          <p className="text-center text-cream/50 text-xs pt-2">
            <Link to="/mot-de-passe-oublie" data-testid="admin-forgot-password-link" className="hover:text-cream underline underline-offset-2">Mot de passe oublié ?</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
